import React, { useEffect, useState } from 'react';

export default function PaymentLinkProductForm({
    product = {},
    onSave,
    onCancel,
    loading = false,
    lockTaxZero = false,
}) {
    const [form, setForm] = useState({
        label: product.label || '',
        quantity: product.quantity ?? 1,
        priceTTC: product.priceTTC ?? 0,
        tax: lockTaxZero ? 0 : (product.tax ?? ''),
        description: product.description || '',
    });

    // Keep TVA locked at 0% whenever lockTaxZero changes or opens
    useEffect(() => {
        if (lockTaxZero && form.tax !== 0) {
            setForm((prev) => ({ ...prev, tax: 0 }));
        }
    }, [lockTaxZero]); // eslint-disable-line react-hooks/exhaustive-deps

    const taxRates = lockTaxZero ? [0] : [20, 10, 5.5, 0];

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const getTotal = () => {
        const price = Number(form.priceTTC || 0);
        const qty = Number(form.quantity || 1);
        return (price * qty).toFixed(2);
    };

    const getHT = () => {
        const priceTTC = Number(form.priceTTC || 0);
        const tax = Number(form.tax || 0);
        if (tax === 0) return priceTTC.toFixed(2);
        return (priceTTC / (1 + tax / 100)).toFixed(2);
    };

    const handleSave = () => {
        const payload = {
            ...form,
            quantity: Number(form.quantity || 1),
            priceTTC: Number(form.priceTTC || 0),
            tax: lockTaxZero ? 0 : (form.tax === '' ? 0 : Number(form.tax)),
        };
        onSave?.(payload);
    };

    return (
        <div className="p-6 bg-gray-50 rounded border mt-6">
            <h2 className="text-xl font-semibold mb-4">Produit</h2>

            <div className="grid md:grid-cols-2 gap-4">
                {/* Label */}
                <div>
                    <label className="block text-sm font-medium mb-1">Nom du produit</label>
                    <input
                        className="border rounded px-3 py-2 w-full"
                        placeholder="Nom"
                        value={form.label || ''}
                        onChange={(e) => handleChange('label', e.target.value)}
                    />
                </div>

                {/* Quantity */}
                <div>
                    <label className="block text-sm font-medium mb-1">Quantité</label>
                    <input
                        className="border rounded px-3 py-2 w-full"
                        type="number"
                        min={1}
                        step={1}
                        value={form.quantity}
                        onChange={(e) =>
                            handleChange('quantity', e.target.value === '' ? '' : Number(e.target.value))
                        }
                    />
                </div>

                {/* Price TTC */}
                <div>
                    <label className="block text-sm font-medium mb-1">Prix TTC unitaire (€)</label>
                    <input
                        className="border rounded px-3 py-2 w-full"
                        type="number"
                        min={0}
                        step="0.01"
                        value={form.priceTTC}
                        onChange={(e) =>
                            handleChange('priceTTC', e.target.value === '' ? '' : Number(e.target.value))
                        }
                    />
                </div>

                {/* Price HT (disabled, auto-calculated) */}
                <div>
                    <label className="block text-sm font-medium mb-1">Prix HT unitaire (€)</label>
                    <input
                        className="border rounded px-3 py-2 w-full bg-gray-100"
                        type="number"
                        value={getHT()}
                        disabled
                    />
                </div>

                {/* Tax */}
                <div>
                    <label className="block text-sm font-medium mb-1">TVA</label>
                    <select
                        className="border rounded px-3 py-2 w-full bg-white"
                        value={String(lockTaxZero ? 0 : (form.tax === '' ? '' : form.tax))}
                        onChange={(e) =>
                            handleChange('tax', e.target.value === '' ? '' : Number(e.target.value))
                        }
                        disabled={lockTaxZero}
                    >
                        {!lockTaxZero && <option value="">Sélectionner TVA</option>}
                        {taxRates.map((rate) => (
                            <option key={rate} value={rate}>
                                {rate}%
                            </option>
                        ))}
                    </select>
                    {lockTaxZero && (
                        <p className="text-xs text-gray-500 mt-1">TVA verrouillée à 0% (débours).</p>
                    )}
                </div>
            </div>

            <div className="mt-4 space-y-1 text-sm">
                <p>
                    Total TTC : <b>{getTotal()}€</b>
                </p>
            </div>

            <div className="mt-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                    className="w-full border rounded px-3 py-2"
                    placeholder="Description du produit"
                    value={form.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                />
            </div>

            <div className="flex justify-center gap-4 pt-6">
                <button className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded" onClick={onCancel}>
                    Annuler
                </button>
                <button
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? (
                        <span className="inline-block animate-spin w-4 h-4 border-2 border-t-transparent border-white rounded-full"></span>
                    ) : form._id || form.label ? (
                        'Sauvegarder le produit'
                    ) : (
                        'Ajouter le produit'
                    )}
                </button>
            </div>
        </div>
    );
}